import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, FilterIcon, SearchIcon, ArrowDownIcon, ArrowUpIcon, FileTextIcon, EyeIcon, PencilIcon, TrashIcon, CheckCircleIcon, TruckIcon, ClockIcon, AlertTriangleIcon, XIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
const Shipments = () => {
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  // Mock data
  const shipments = [{
    id: 'SH-12345',
    origin: 'Chicago, IL',
    destination: 'Dallas, TX',
    status: 'In Transit',
    createdAt: '2023-05-15',
    pickupDate: '2023-05-16',
    deliveryDate: '2023-05-18',
    customer: 'Acme Corp',
    carrier: 'Fast Freight Inc.',
    weight: '5,200 lbs',
    statusColor: 'blue'
  }, {
    id: 'SH-12344',
    origin: 'New York, NY',
    destination: 'Miami, FL',
    status: 'Delivered',
    createdAt: '2023-05-14',
    pickupDate: '2023-05-14',
    deliveryDate: '2023-05-16',
    customer: 'Global Industries',
    carrier: 'Speedy Logistics',
    weight: '3,800 lbs',
    statusColor: 'green'
  }, {
    id: 'SH-12343',
    origin: 'Seattle, WA',
    destination: 'Los Angeles, CA',
    status: 'Pending Pickup',
    createdAt: '2023-05-13',
    pickupDate: '2023-05-16',
    deliveryDate: '2023-05-17',
    customer: 'Pacific Trading Co',
    carrier: 'West Coast Transport',
    weight: '2,950 lbs',
    statusColor: 'amber'
  }, {
    id: 'SH-12342',
    origin: 'Boston, MA',
    destination: 'Washington, DC',
    status: 'Delayed',
    createdAt: '2023-05-12',
    pickupDate: '2023-05-13',
    deliveryDate: '2023-05-15',
    customer: 'Eastern Supplies',
    carrier: 'Capital Freight',
    weight: '4,100 lbs',
    statusColor: 'red'
  }, {
    id: 'SH-12341',
    origin: 'Denver, CO',
    destination: 'Phoenix, AZ',
    status: 'Delivered',
    createdAt: '2023-05-10',
    pickupDate: '2023-05-11',
    deliveryDate: '2023-05-12',
    customer: 'Mountain Distributors',
    carrier: 'Desert Express',
    weight: '6,300 lbs',
    statusColor: 'green'
  }, {
    id: 'SH-12340',
    origin: 'Atlanta, GA',
    destination: 'Nashville, TN',
    status: 'Delivered',
    createdAt: '2023-05-09',
    pickupDate: '2023-05-10',
    deliveryDate: '2023-05-11',
    customer: 'Southern Manufacturing',
    carrier: 'Music City Transport',
    weight: '4,750 lbs',
    statusColor: 'green'
  }, {
    id: 'SH-12339',
    origin: 'Portland, OR',
    destination: 'San Francisco, CA',
    status: 'In Transit',
    createdAt: '2023-05-14',
    pickupDate: '2023-05-15',
    deliveryDate: '2023-05-16',
    customer: 'NorthWest Goods',
    carrier: 'Pacific Highway Logistics',
    weight: '3,200 lbs',
    statusColor: 'blue'
  }];
  // Simulate loading data
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const toggleStatus = (status: string) => {
    if (selectedStatus.includes(status)) {
      setSelectedStatus(selectedStatus.filter(s => s !== status));
    } else {
      setSelectedStatus([...selectedStatus, status]);
    }
  };
  const clearFilters = () => {
    setSelectedStatus([]);
    setDateRange({
      from: '',
      to: ''
    });
    setSearchTerm('');
  };
  // Filter shipments based on search and filters
  const filteredShipments = shipments.filter(shipment => {
    // Search term filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' || shipment.id.toLowerCase().includes(searchLower) || shipment.origin.toLowerCase().includes(searchLower) || shipment.destination.toLowerCase().includes(searchLower) || shipment.customer.toLowerCase().includes(searchLower);
    // Status filter
    const matchesStatus = selectedStatus.length === 0 || selectedStatus.includes(shipment.status);
    // Date range filter
    const shipmentDate = new Date(shipment.createdAt);
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;
    const matchesDateFrom = !fromDate || shipmentDate >= fromDate;
    const matchesDateTo = !toDate || shipmentDate <= toDate;
    return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
  });
  // Sort shipments
  const sortedShipments = [...filteredShipments].sort((a, b) => {
    if (a[sortField as keyof typeof a] < b[sortField as keyof typeof b]) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (a[sortField as keyof typeof a] > b[sortField as keyof typeof b]) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircleIcon size={16} className="text-green-500" data-id="element-1417" />;
      case 'In Transit':
        return <TruckIcon size={16} className="text-blue-500" data-id="element-1418" />;
      case 'Pending Pickup':
        return <ClockIcon size={16} className="text-amber-500" data-id="element-1419" />;
      case 'Delayed':
        return <AlertTriangleIcon size={16} className="text-red-500" data-id="element-1420" />;
      default:
        return null;
    }
  };
  const renderSkeleton = () => <div className="animate-pulse" data-id="element-1421">
      <div className="h-10 bg-gray-200 rounded mb-4 w-full" data-id="element-1422"></div>
      <div className="space-y-4" data-id="element-1423">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-16 bg-gray-200 rounded w-full" data-id="element-1424"></div>)}
      </div>
    </div>;
  return <div className="py-4" data-id="element-1425">
      <div className="mb-6" data-id="element-1426">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-1427">Shipments</h1>
        <p className="text-gray-600" data-id="element-1428">Manage and track your shipments</p>
      </div>
      {/* Actions and Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-1429">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0" data-id="element-1430">
          <div className="flex items-center space-x-2 flex-1" data-id="element-1431">
            <div className="relative flex-1 max-w-md" data-id="element-1432">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1433">
                <SearchIcon size={16} className="text-gray-400" data-id="element-1434" />
              </div>
              <input type="text" placeholder="Search by ID, origin, destination, or customer..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1435" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`px-3 py-2 border rounded-md text-sm font-medium flex items-center transition-colors ${showFilters ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1436">
              <FilterIcon size={16} className="mr-2" data-id="element-1437" />
              Filters
              {showFilters ? <ChevronUpIcon size={16} className="ml-2" data-id="element-1438" /> : <ChevronDownIcon size={16} className="ml-2" data-id="element-1439" />}
            </button>
          </div>
          <div className="flex items-center space-x-3" data-id="element-1440">
            <button onClick={() => {}} className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-1441">
              <FileTextIcon size={16} className="mr-2" data-id="element-1442" />
              Export
            </button>
          </div>
        </div>
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1443">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1444">
              <div data-id="element-1445">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1446">
                  Status
                </label>
                <div className="space-y-2" data-id="element-1447">
                  {['In Transit', 'Delivered', 'Pending Pickup', 'Delayed'].map(status => <div key={status} className="flex items-center" data-id="element-1448">
                        <input type="checkbox" id={`status-${status}`} checked={selectedStatus.includes(status)} onChange={() => toggleStatus(status)} className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" data-id="element-1449" />
                        <label htmlFor={`status-${status}`} className="ml-2 text-sm text-gray-700" data-id="element-1450">
                          {status}
                        </label>
                      </div>)}
                </div>
              </div>
              <div data-id="element-1451">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1452">
                  Date Range
                </label>
                <div className="space-y-2" data-id="element-1453">
                  <input type="date" value={dateRange.from} onChange={e => setDateRange({
                ...dateRange,
                from: e.target.value
              })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1454" />
                  <input type="date" value={dateRange.to} onChange={e => setDateRange({
                ...dateRange,
                to: e.target.value
              })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500" data-id="element-1455" />
                </div>
              </div>
              <div className="flex items-end" data-id="element-1456">
                <button onClick={clearFilters} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-1457">
                  Clear Filters
                </button>
              </div>
            </div>
          </div>}
      </div>
      {loading ? renderSkeleton() : <>
          {/* Results count */}
          <div className="mb-4 text-sm text-gray-600" data-id="element-1458">
            Showing {sortedShipments.length} of {shipments.length} shipments
          </div>
          {/* Shipments Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-1459">
            <div className="overflow-x-auto" data-id="element-1460">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1461">
                <thead data-id="element-1462">
                  <tr className="bg-gray-50" data-id="element-1463">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('id')} data-id="element-1464">
                      <div className="flex items-center" data-id="element-1465">
                        Shipment ID
                        {sortField === 'id' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-1466" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-1467" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('createdAt')} data-id="element-1468">
                      <div className="flex items-center" data-id="element-1469">
                        Date
                        {sortField === 'createdAt' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-1470" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-1471" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1472">
                      Route
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700" onClick={() => handleSort('status')} data-id="element-1473">
                      <div className="flex items-center" data-id="element-1474">
                        Status
                        {sortField === 'status' && (sortDirection === 'asc' ? <ArrowUpIcon size={14} className="ml-1" data-id="element-1475" /> : <ArrowDownIcon size={14} className="ml-1" data-id="element-1476" />)}
                      </div>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1477">
                      Customer
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1478">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1479">
                  {sortedShipments.length > 0 ? sortedShipments.map(shipment => <tr key={shipment.id} className="hover:bg-gray-50 transition-colors" data-id="element-1480">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1481">
                          <Link to={`/shipments/${shipment.id}`} className="text-primary-700 hover:text-primary-800 font-medium" data-id="element-1482">
                            {shipment.id}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1483">
                          {new Date(shipment.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1484">
                          <div className="text-sm text-gray-900" data-id="element-1485">
                            {shipment.origin}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1486">
                            → {shipment.destination}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-1487">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${shipment.status === 'Delivered' ? 'bg-green-100 text-green-800' : shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : shipment.status === 'Pending Pickup' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`} data-id="element-1488">
                            {getStatusIcon(shipment.status)}
                            <span className="ml-1.5" data-id="element-1489">{shipment.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1490">
                          {shipment.customer}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1491">
                          <div className="flex justify-end items-center space-x-3" data-id="element-1492">
                            <Link to={`/shipments/${shipment.id}`} className="text-gray-400 hover:text-primary-700 transition-colors" title="View Details" data-id="element-1493">
                              <EyeIcon size={18} data-id="element-1494" />
                            </Link>
                            <button className="text-gray-400 hover:text-primary-700 transition-colors" title="Edit" data-id="element-1495">
                              <PencilIcon size={18} data-id="element-1496" />
                            </button>
                            <button className="text-gray-400 hover:text-red-600 transition-colors" title="Delete" data-id="element-1497">
                              <TrashIcon size={18} data-id="element-1498" />
                            </button>
                          </div>
                        </td>
                      </tr>) : <tr data-id="element-1499">
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500 bg-gray-50" data-id="element-1500">
                        <div className="flex flex-col items-center" data-id="element-1501">
                          <SearchIcon size={24} className="text-gray-400 mb-2" data-id="element-1502" />
                          <p className="text-gray-600 font-medium" data-id="element-1503">
                            No shipments found
                          </p>
                          <p className="text-gray-500 text-sm" data-id="element-1504">
                            Try adjusting your search or filter criteria
                          </p>
                        </div>
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between" data-id="element-1505">
            <div className="flex-1 flex justify-between sm:hidden" data-id="element-1506">
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1507">
                Previous
              </button>
              <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-1508">
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-1509">
              <div data-id="element-1510">
                <p className="text-sm text-gray-700" data-id="element-1511">
                  Showing page 1 of {Math.ceil(sortedShipments.length / 10)}
                </p>
              </div>
              <div data-id="element-1512">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-1513">
                  <button className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1514">
                    Previous
                  </button>
                  <button className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-1515">
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </>}
    </div>;
};
export default Shipments;